"use client";

import React from "react";
import { Paperclip } from "lucide-react";

import { SupportStatusBadge } from "./SupportStatusBadge";
import {
  avatarTone,
  displayFrom,
  formatWhen,
  initialsOf,
  threadHasAttachments,
} from "./support-inbox-format";
import type {
  SupportInboxCorrespondent,
  SupportInboxThreadSummary,
} from "./types";

interface SupportInboxThreadListProps {
  /** How many threads exist before the search filter — drives the empty copy. */
  totalThreadCount: number;
  filteredThreads: SupportInboxThreadSummary[];
  query: string;
  selectedThreadId: string | null;
  correspondentByThread: ReadonlyMap<string, SupportInboxCorrespondent | null>;
  onSelectThread: (threadId: string) => void;
  onPrefetchThread: (threadId: string) => void;
  /** The list gives way to the reading pane on mobile once a thread is open. */
  visibleOnMobile: boolean;
}

/** Thread list (left column) for the Support Inbox tab. */
export function SupportInboxThreadList({
  totalThreadCount,
  filteredThreads,
  query,
  selectedThreadId,
  correspondentByThread,
  onSelectThread,
  onPrefetchThread,
  visibleOnMobile,
}: SupportInboxThreadListProps) {
  return (
    <div
      className={`md:w-2/5 md:max-w-md md:overflow-y-auto md:border-r md:border-border md:pr-3 ${
        visibleOnMobile ? "block" : "hidden md:block"
      }`}
    >
      {totalThreadCount === 0 ? (
        <p className="text-sm text-muted-foreground p-2">
          No support email yet.
        </p>
      ) : filteredThreads.length === 0 ? (
        <p className="text-sm text-muted-foreground p-2">
          No conversations match “{query}”.
        </p>
      ) : (
        <ul className="space-y-1">
          {filteredThreads.map((thread) => {
            const isSelected = thread.threadId === selectedThreadId;
            const correspondent = correspondentByThread.get(thread.threadId);
            const who =
              correspondent?.name ||
              correspondent?.email ||
              displayFrom(thread.from);
            return (
              <li key={thread.threadId}>
                <button
                  type="button"
                  onClick={() => onSelectThread(thread.threadId)}
                  onMouseEnter={() => onPrefetchThread(thread.threadId)}
                  onFocus={() => onPrefetchThread(thread.threadId)}
                  className={`flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-transparent hover:bg-muted/50"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarTone(
                      who,
                    )}`}
                    aria-hidden
                  >
                    {initialsOf(who)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-card-foreground">
                        {who}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {formatWhen(thread.latestReceivedAt)}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-card-foreground/80">
                      {thread.subject || "(no subject)"}
                    </span>
                    <span className="mt-1 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1 truncate text-[11px] text-muted-foreground">
                        {threadHasAttachments(thread) && (
                          <Paperclip
                            className="h-3 w-3 shrink-0"
                            aria-label="Has attachments"
                          />
                        )}
                        {thread.messageCount > 1
                          ? `${thread.messageCount} messages`
                          : "1 message"}
                      </span>
                      <SupportStatusBadge status={thread.status} />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
